import { Box, Collapse, IconButton, Stack, styled, Typography, useTheme } from '@mui/material';
import { memo, ReactNode, useMemo, useState } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import { isNullish } from 'remeda';
import { DynamicIcon, Tooltip } from 'src/components';
import { WorkflowNodeDisplayData } from 'src/pages/Settings/workflows/utils/workflow-helpers';

export interface SourceHandleConfig {
  id?: string;
  left?: number | string;
}

interface BaseNodeProps extends NodeProps<WorkflowNodeDisplayData> {
  content?: ReactNode;
  footer?: ReactNode;
  sourceHandles?: SourceHandleConfig[];
}

const NODE_WIDTH = 280;
const DETAILS_MAX_HEIGHT = 240;

const NodeWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${
    selected ? theme.palette.custom.OV.sys.primary : theme.palette.custom.OV.sys.outlineVariant
  }`,
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
  padding: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  width: NODE_WIDTH,
  boxShadow: selected ? theme.shadows[5] : theme.shadows[1],
  position: 'relative',
  zIndex: selected ? 20 : 1,
  cursor: 'grab',
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    boxShadow: theme.shadows[6],
    transform: 'translateY(-2px)',
  },
  '&:hover .workflow-node__icon': {
    boxShadow: theme.shadows[3],
    transform: 'translateY(-1px) rotate(var(--workflow-icon-rotation, 0deg))',
  },
}));

const Header = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'headerColor',
})<{ headerColor: string }>(({ theme, headerColor }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  backgroundColor: headerColor,
  borderRadius: theme.shape.borderRadius,
  paddingInline: theme.spacing(1),
  paddingBlock: theme.spacing(0.5),
  columnGap: theme.spacing(1),
}));

const IconWrapper = styled(Stack)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.action.hover,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: theme.shadows[1],
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.shorter,
  }),
}));

const DescriptionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{ backgroundColor: string }>(({ theme, backgroundColor }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor,
  padding: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const DetailsContainer = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  backgroundColor: theme.palette.custom.OV.sys.surface,
  padding: theme.spacing(1),
  marginTop: theme.spacing(1),
  maxHeight: DETAILS_MAX_HEIGHT,
  overflowY: 'auto',
}));

const NodeTitle = styled(Typography)(({ theme }) => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 600,
  color:
    theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
}));

const RawDataWrapper = styled('pre')(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(0.5),
  fontSize: '0.75rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

const hiddenHandleStyle = {
  opacity: 0,
  border: 'none',
} as const;

const BaseNodeComponent = ({
  data,
  selected,
  content,
  footer,
  sourceHandles,
}: BaseNodeProps) => {
  const theme = useTheme();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const headerColor = data.headerColor ?? theme.palette.custom.OV.sys.surfaceContainerHigh;
  const summaryBackground = data.accentColor ?? theme.palette.custom.OV.sys.surfaceContainer;
  const detailsTooltip = detailsOpen ? 'Collapse details' : 'Expand details';

  const nodeIconRotationStyle = useMemo(
    () =>
      ({
        '--workflow-icon-rotation': `${Math.random() * 4 - 2}deg`,
      }) as React.CSSProperties,
    [],
  );

  const renderSourceHandles = () => {
    if (!sourceHandles?.length) {
      return (
        <Handle
          type="source"
          position={Position.Bottom}
          style={hiddenHandleStyle}
          isConnectable={false}
        />
      );
    }

    return sourceHandles.map((handle, index) => (
      <Handle
        key={handle.id ?? `source-${index}`}
        id={handle.id}
        type="source"
        position={Position.Bottom}
        style={{
          ...hiddenHandleStyle,
          left: handle.left ?? '50%',
        }}
        isConnectable={false}
      />
    ));
  };

  const descriptionContent = content ?? data.description;
  const description = isNullish(descriptionContent) ? null : (
    <DescriptionContainer backgroundColor={summaryBackground}>
      {typeof descriptionContent === 'string' ? (
        <Typography variant="body2" color="text.primary">
          {descriptionContent}
        </Typography>
      ) : (
        descriptionContent
      )}
    </DescriptionContainer>
  );

  const detailContent = (
    <DetailsContainer
      className="nowheel"
      onWheelCapture={(event) => {
        // keep scroll gestures inside the detail area from triggering canvas pan/zoom
        event.stopPropagation();
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        Node ID: {data.nodeId}
      </Typography>
      {data.rawData && Object.keys(data.rawData).length ? (
        // for MVP purposes, we just dump the raw data as JSON
        <RawDataWrapper>{JSON.stringify(data.rawData, null, 2)}</RawDataWrapper>
      ) : (
        <Typography variant="body2">No additional details.</Typography>
      )}
    </DetailsContainer>
  );

  return (
    <NodeWrapper selected={selected} style={nodeIconRotationStyle}>
      <Handle
        type="target"
        position={Position.Top}
        style={hiddenHandleStyle}
        isConnectable={false}
      />
      {renderSourceHandles()}
      <Header headerColor={headerColor}>
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          {data.icon ? (
            <IconWrapper className="workflow-node__icon">
              <DynamicIcon
                name={data.icon}
                sx={{
                  color:
                    data.iconColor ??
                    (theme.palette.mode === 'dark'
                      ? theme.palette.common.white
                      : theme.palette.text.primary),
                }}
              />
            </IconWrapper>
          ) : null}
          <NodeTitle variant="subtitle2" title={data.title}>
            {data.title}
          </NodeTitle>
        </Stack>
        <Tooltip title={detailsTooltip}>
          <IconButton
            size="small"
            onClick={() => setDetailsOpen((previous) => !previous)}
            aria-label={detailsTooltip}
            aria-expanded={detailsOpen}
          >
            <DynamicIcon
              name="expand_more"
              sx={{
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shorter,
                }),
                transform: detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </IconButton>
        </Tooltip>
      </Header>
      {description}
      <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
        {detailContent}
      </Collapse>
      {footer}
    </NodeWrapper>
  );
};

export const BaseNode = memo(BaseNodeComponent);
